class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    initSubscribeAndSaveToggles();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => { 
      event.preventDefault(); 
      this.renderContents(); 
    });
    cartLink.addEventListener('keydown', (event) => { 
      if (event.code.toUpperCase() === 'SPACE') { 
        event.preventDefault(); 
        this.renderContents(); 
      } 
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    
    setTimeout(() => { 
      this.classList.add('animate', 'active'); 
      window.markCartDrawerLoaded?.(window.cartDrawerStartTime);
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') 
        ? this.querySelector('.drawer__inner-empty') 
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => { 
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open')); 
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // --- VIP TOGGLE LOGIC ---
  initVipToggle() {
    const vipCheckbox = this.querySelector('#perk_cart_toggle');
    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    const vipBtn = this.querySelector('#vip-checkout-002');
    const checkboxKey = 'myCheckboxState';

    if (!vipCheckbox || !checkoutBtn || !vipBtn) return;

    // Load state
    const storedState = sessionStorage.getItem(checkboxKey);
    if (storedState !== null) {
      const isActive = JSON.parse(storedState);
      vipCheckbox.checked = isActive;
      this.toggleVipButtons(isActive, checkoutBtn, vipBtn);
    }

    // Listen for changes
    vipCheckbox.addEventListener('change', (e) => {
      const isActive = e.target.checked;
      sessionStorage.setItem(checkboxKey, JSON.stringify(isActive));
      this.toggleVipButtons(isActive, checkoutBtn, vipBtn);
    });
  }

  toggleVipButtons(isActive, standard, vip) {
    if (isActive) {
      standard.classList.add('checkout_hide');
      vip.classList.remove('checkout_hide');
    } else {
      standard.classList.remove('checkout_hide');
      vip.classList.add('checkout_hide');
    }
  }

  // UPDATED: Added shouldOpen parameter to prevent auto-opening during background refreshes
  renderContents(parsedState, shouldOpen = true) {
    window.cartDrawerStartTime = performance.now();

    if (parsedState && parsedState.sections) {
      this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
      
      StampedFn.reloadUGC();
      this.initVipToggle();
      fetchCartItemsCount();
      runCartWaterfall();
      
      setTimeout(() => { 
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this)); 
        if (shouldOpen) this.open(); 
      });
    } else {
      this.classList.remove('is-empty');
      if (this.querySelector('.drawer__inner')) {
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      }
      
      fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const innerDrawer = document.querySelector('#CartDrawer');
          if (innerDrawer && html.querySelector('#CartDrawer')) {
            innerDrawer.innerHTML = html.querySelector('#CartDrawer').innerHTML;
          }
          this.querySelector('.drawer__inner').classList.remove('is-empty');
        })
        .finally(() => {
          StampedFn.reloadUGC();
          this.initVipToggle();
          fetchCartItemsCount();
          runCartWaterfall();
          
          setTimeout(() => { 
            this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this)); 
            if (shouldOpen) this.open(); 
          });
        });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') { 
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML; 
  }
  
  getSectionsToRender() { 
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; 
  }
  
  setActiveElement(element) { 
    this.activeElement = element; 
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() { 
    return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }, { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }]; 
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/************ BUNDLE COUNT ************/
// CART ITEMS COUNT - Adaptation for bundles - counts them as one item not multiples
function fetchCartItemsCount(bundleHandler = false) {
  fetch('/cart.js', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  })
  .then((response) => response.json())
  .then((cart) => {
    const uniqueItems = {};

    cart.items.forEach((item) => {
      if (item.properties && item.properties._rc_bundle) {
        const bundleId = item.properties._rc_bundle;
        if (!uniqueItems[bundleId]) {
          uniqueItems[bundleId] = item.quantity;
        }
      } else {
        uniqueItems[item.key] = item.quantity;
      }
    });
    
    const cDW = document.querySelector('cart-drawer');
    const totalUniqueProducts = Object.keys(uniqueItems).length;
    const counterContainer = document.querySelector('.cart-count-bubble [aria-hidden="true"]');

    if (counterContainer) {
      counterContainer.textContent = `${totalUniqueProducts > 0 ? totalUniqueProducts : ""}`;
    }
    
    if (bundleHandler && cDW) {
      setTimeout(() => {
        cDW.classList.toggle('is-empty', totalUniqueProducts === 0);
      }, 500);
    }
  })
  .catch((error) => console.error('Error fetching cart:', error));
}

fetchCartItemsCount();


/********** GWP WATERFALL + RESTRICTED REMOVAL **********/
let isWaterfallRunning = false;

async function runCartWaterfall() {
  if (isWaterfallRunning) return;
  isWaterfallRunning = true;
  
  try {
    console.log('--- Logic Waterfall Started ---');
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await response.json();
    if (!cart) throw new Error("No cart data");

    // PRIMARY CLEANUP (Remove specific IDs no matter what) ---
    const primaryCleanupIds = ["41954799419467", "41295598387275", "42549135736907", "42549135769675", "42549282013259", "42549282897995", "42549283749963", "42549284536395"];
    const itemsToRemove = cart.items.filter(item => primaryCleanupIds.includes(String(item.variant_id)));
    
    if (itemsToRemove.length > 0) {
      console.log('🧹 Primary Cleanup: Removing restricted items:', primaryCleanupIds);
      const updates = {};
      itemsToRemove.forEach(item => { updates[item.key] = 0; });
      
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      isWaterfallRunning = false;
      refreshCartUI(false); // Pass false to prevent auto-opening
      return;
    }

    // 1. EVALUATE FLAGS MANUALLY FROM CART DATA
    let liveNCV2 = false;
    let liveNCV1 = false;
    let liveKit  = false;

    const kitVariants = ["41358312865867", "42164395540555"];
    const allAppliedCodes = [];

    if (cart.discount_applications) {
      cart.discount_applications.forEach(d => allAppliedCodes.push((d.code || "").toUpperCase()));
    }

    cart.items.forEach(item => {
      if (kitVariants.includes(String(item.variant_id))) liveKit = true;
      if (item.line_level_discount_allocations) {
        item.line_level_discount_allocations.forEach(a => {
          allAppliedCodes.push((a.discount_application.code || a.discount_application.title || "").toUpperCase());
        });
      }
    });

    const ncv2Keywords = ["NCFREEGIFTS", "NC-PICK", "NC20OF", "NC30OF", "BUNDLE-NC", "WELCOME-NC", "START25"];
    const ncv1Keywords = ["NEW20", "NEW30"];

    allAppliedCodes.forEach(code => {
      if (ncv2Keywords.some(key => code.includes(key))) liveNCV2 = true;
      if (ncv1Keywords.some(key => code.includes(key))) liveNCV1 = true;
    });

    console.log(`Flags Found -> NCV2: ${liveNCV2} | NCV1: ${liveNCV1} | Kit: ${liveKit}`);

    // 2. SET ACTIVE GIFT STRUCTURE BASED ON HIERARCHY
    let activeGifts = [];

    if (liveNCV2) {
      console.log("Structure Active: NCV2 (No Gifts)");
    } 
    else if (liveNCV1) {
      console.log("Structure Active: NCV1");
      if (typeof nc_gwp01_id !== 'undefined') activeGifts.push({ id: nc_gwp01_id, price: nc_gwp01_price });
      if (typeof nc_gwp02_id !== 'undefined') activeGifts.push({ id: nc_gwp02_id, price: nc_gwp02_price });
      if (typeof nc_gwp03_id !== 'undefined') activeGifts.push({ id: nc_gwp03_id, price: nc_gwp03_price });
    } 
    else if (liveKit) {
      console.log("Structure Active: Kit");
      if (typeof kit_gwp01_id !== 'undefined') activeGifts.push({ id: kit_gwp01_id, price: kit_gwp01_price });
      if (typeof kit_gwp02_id !== 'undefined') activeGifts.push({ id: kit_gwp02_id, price: kit_gwp02_price });
      if (typeof kit_gwp03_id !== 'undefined') activeGifts.push({ id: kit_gwp03_id, price: kit_gwp03_price });
    } 
    else {
      console.log("Structure Active: Sitewide");
      if (typeof sw_gwp01_id !== 'undefined') activeGifts.push({ id: sw_gwp01_id, price: sw_gwp01_price });
      if (typeof sw_gwp02_id !== 'undefined') activeGifts.push({ id: sw_gwp02_id, price: sw_gwp02_price });
      if (typeof sw_gwp03_id !== 'undefined') activeGifts.push({ id: sw_gwp03_id, price: sw_gwp03_price });
    }

    // 3. RESTRICTED ITEM CLEANUP
    const restrictionFound = await checkRestrictedNCItems(cart);
    if (restrictionFound) return; 

    // 4. GATHER ALL POTENTIAL IDS FOR CLEANUP
    const allPotentialGifts = [
      typeof nc_gwp01_id !== 'undefined' ? nc_gwp01_id : null,
      typeof nc_gwp02_id !== 'undefined' ? nc_gwp02_id : null,
      typeof nc_gwp03_id !== 'undefined' ? nc_gwp03_id : null,
      typeof kit_gwp01_id !== 'undefined' ? kit_gwp01_id : null,
      typeof kit_gwp02_id !== 'undefined' ? kit_gwp02_id : null,
      typeof kit_gwp03_id !== 'undefined' ? kit_gwp03_id : null,
      typeof sw_gwp01_id !== 'undefined' ? sw_gwp01_id : null,
      typeof sw_gwp02_id !== 'undefined' ? sw_gwp02_id : null,
      typeof sw_gwp03_id !== 'undefined' ? sw_gwp03_id : null
    ].filter(id => id != null).map(id => String(id));

    const cartData = {
      totalPrice: cart.total_price / 100,
      productLists: cart.items.map(item => String(item.variant_id))
    };

    await processGwpLogic(cartData, activeGifts, allPotentialGifts, liveNCV1, liveNCV2);

  } catch (error) {
    console.error('Waterfall Error:', error);
  } finally {
    isWaterfallRunning = false;
  }
}

/** RESTRICTED ITEM CHECK **/
async function checkRestrictedNCItems(cart) {
  const blacklistedIds = ["41501513252939", "41295600975947", "42123826692171", "42123832852555"];
  const restrictedNCVariants = ["42142473060427", "42142473125963", "42142473093195", "42142473027659", "42263650402379", "42263622680651", "42263650369611", "42549282013259", "42549282897995", "42549283749963", "42549284536395", "42778523893835", "42778551025739", "42778567573579", "42892017827915", "42892064063563"];
  const newcustomerDiscounts = ['BUNDLE-NC-67OFF', 'BUNDLE-NC-57OFF', 'BUNDLE-NC-51OFF', 'BUNDLE-NC-43OFF', 'WELCOME-NC-15OFF', 'WELCOME-NC-40OFF', 'WELCOME-NC-53OFF', 'WELCOME-NC-62OFF', 'NC20OFF', 'NC30OFF', 'NC-PICK1-SNS', 'NC-PICK2', 'NC-PICK2-SNS', 'NC-PICK3', 'NC-PICK3-SNS', 'NC-PICK4', 'NC-PICK4-SNS', 'NC-30PERCENT', 'NC-50PERCENT', 'START25'];
  const stickyCodes = ['NC20OFF', 'NC30OFF', 'NC-PICK1-SNS', 'NC-PICK2', 'NC-PICK2-SNS', 'NC-PICK3', 'NC-PICK3-SNS', 'NC-PICK4', 'NC-PICK4-SNS', 'NC-30PERCENT', 'NC-50PERCENT'];
  const codesOnItems = [];

  /** OLD
  cart.items.forEach(item => {
    (item.line_level_discount_allocations || []).forEach(a => {
      codesOnItems.push((a.discount_application.code || a.discount_application.title || "").toUpperCase());
    });
  });
  **/

  /**NEW**/
  cart.items.forEach(item => {
    (item.line_level_discount_allocations || []).forEach(a => {
      codesOnItems.push((a.discount_application.code || a.discount_application.title || "").toUpperCase());
    });
    // Treat the presence of this variant as an active NC20OFF session
    if (String(item.variant_id) === "42779027636299") {
      codesOnItems.push("NC20OFF");
    }
  });

  const appliedCodes = (cart.discount_applications || []).map(d => (d.code || "").toUpperCase());
  const allActiveCodes = [...new Set([...appliedCodes, ...codesOnItems])].filter(Boolean);
  const giftsInCart = cart.items.filter(i => restrictedNCVariants.includes(String(i.variant_id)));
  
  if (giftsInCart.length === 0) return false;

  const activeNC = stickyCodes.find(code => codesOnItems.includes(code));
  if (activeNC) {
    /**OLD
    const currentNCQuantity = cart.items.reduce((acc, item) => {
      const vId = String(item.variant_id);
      const isGift = restrictedNCVariants.includes(vId) || blacklistedIds.includes(vId);
      const hasCode = item.line_level_discount_allocations && item.line_level_discount_allocations.some(a => (a.discount_application.code || a.discount_application.title || "").toUpperCase() === activeNC);
      return !isGift && hasCode ? acc + item.quantity : acc;
    }, 0);
    **/
   /**NEW**/
   const currentNCQuantity = cart.items.reduce((acc, item) => {
      const vId = String(item.variant_id);
      const isGift = restrictedNCVariants.includes(vId) || blacklistedIds.includes(vId);
      const hasCode = item.line_level_discount_allocations && item.line_level_discount_allocations.some(a => (a.discount_application.code || a.discount_application.title || "").toUpperCase() === activeNC);
      
      // Count if it has the actual code OR if it is our qualifying variant
      const isQualifyingItem = (!isGift && hasCode) || vId === "42779027636299";
      
      return isQualifyingItem ? acc + item.quantity : acc;
    }, 0);


    if (window.lastNCQty !== undefined && currentNCQuantity < window.lastNCQty) {
      window.lastNCQty = currentNCQuantity;
      await executeCleanup(cart, true, restrictedNCVariants, blacklistedIds);
      return true;
    }
    window.lastNCQty = currentNCQuantity;
  }

  const activeNCCode = newcustomerDiscounts.find(nc => allActiveCodes.some(active => active.includes(nc.toUpperCase())));
  const ncCodeIsAssigned = newcustomerDiscounts.some(nc => codesOnItems.includes(nc.toUpperCase()));
  const hasQualifyingVariant = cart.items.some(item => String(item.variant_id) === "42779027636299");

  if (!hasQualifyingVariant && (!activeNCCode || (activeNCCode && !ncCodeIsAssigned))) {
    await executeCleanup(cart, true, restrictedNCVariants, blacklistedIds);
    return true;
  }

  return false;
}

async function executeCleanup(cart, clearAll, restricted, blacklisted) {
  if (clearAll) await fetch("/discount/CLEAR");
  const updates = {};
  cart.items.forEach(item => {
    const vId = String(item.variant_id);
    if (restricted.includes(vId) || blacklisted.includes(vId)) updates[item.key] = 0;
  });
  if (Object.keys(updates).length > 0) {
    await fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates }) });
    refreshCartUI(false);
  }
}

async function processGwpLogic(cartData, activeGifts, allPotentialGifts, liveNCV1, liveNCV2) {
  let updatesMade = false;
  const updates = {};
  const kitVariants = ["41358312865867", "42164395540555"];
  
  const eligibleGiftIds = activeGifts
    .filter(gift => cartData.totalPrice >= gift.price)
    .map(gift => String(gift.id));

  // 1. Remove Kits if NC offer is active
  if (liveNCV1 || liveNCV2) {
    kitVariants.forEach(kitId => {
      if (cartData.productLists.includes(kitId)) {
        updates[kitId] = 0;
        updatesMade = true;
        console.log(`Court Cleanup: Removing Kit ${kitId} because NC offer is active`);
      }
    });
  }

  // 2. Mark old/ineligible gifts for removal (Set quantity to 0)
  allPotentialGifts.forEach(id => {
    if (cartData.productLists.includes(id) && !eligibleGiftIds.includes(id)) {
      updates[id] = 0;
      updatesMade = true;
    }
  });

  // 3. Mark new eligible gifts for addition (Set quantity to 1)
  eligibleGiftIds.forEach(id => {
    if (!cartData.productLists.includes(id)) {
      updates[id] = 1;
      updatesMade = true;
    }
  });

  if (updatesMade) {
    console.log('🔄 Syncing GWPs:', updates);
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      if (response.ok) {
        refreshCartUI(false);
      }
    } catch (error) {
      console.error('GWP Sync Error:', error);
    }
  }
}

function refreshCartUI(shouldOpen = true) {
  const isCartPage = window.location.pathname.endsWith('/cart');
  const drawer = document.querySelector('cart-drawer');
  const cartItems = document.querySelector('cart-items');

  if (isCartPage && cartItems) {
    console.log('--- Refreshing Cart Page UI via AJAX ---');
    cartItems.onCartUpdate(); // Dawn's native cart page update handle
  } else if (drawer) {
    console.log('--- Refreshing Drawer UI ---');
    drawer.renderContents(null, shouldOpen);
    document.dispatchEvent(new CustomEvent('cart:synced'));
  } else {
    window.location.reload(); // Fallback if components fail
  }
}

// Loading VIP toggle State
function loadTogglestate() {
  const vipCheckbox = document.getElementById('perk_cart_toggle');
  const checkboxKey = 'myCheckboxState';
  if (vipCheckbox) {
    window.addEventListener('load', () => {
      const storedState = sessionStorage.getItem(checkboxKey);
      if (storedState) {
        vipCheckbox.checked = JSON.parse(storedState);
        if (vipCheckbox.checked) {
          const checkoutButton = document.getElementById('CartDrawer-Checkout');
          const vipcheckoutButton = document.getElementById('Cart-Drawer-VIP-Checkout');
          if (checkoutButton) checkoutButton.classList.add('checkout_hide');
          if (vipcheckoutButton) vipcheckoutButton.classList.remove('checkout_hide');
        }
      }
    });
  }
}
loadTogglestate();


/********** BUNDLE REMOVAL ***********/
document.addEventListener('click', function(event) {
  // 1. Detect click on the remove button inside the cart-remove-button component
  const removeBtn = event.target.closest('cart-remove-button button');
  if (!removeBtn) return;

  // 2. Check if the parent component has our bundle ID
  const parentComponent = removeBtn.closest('cart-remove-button');
  const bundleId = parentComponent ? parentComponent.dataset.bundleId : null;

  // 3. If it's a bundle continue
  if (bundleId && bundleId !== "") {
    event.preventDefault();
    event.stopImmediatePropagation();

    console.log('Bundle removal triggered for ID:', bundleId);

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const updates = {};
        let found = false;

        cart.items.forEach(item => {
          if (item.properties && item.properties._rc_bundle === bundleId) {
            updates[item.key] = 0;
            found = true;
          }
        });

        if (found) {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }
      })
      .then(response => {
        if (response) {
          console.log('Bundle cleared. Refreshing UI...');
          refreshCartUI(true);
        }
      })
      .catch(error => console.error('Bundle removal error:', error));
  }
}, true);


/******** SUBSCRIBE AND SAVE TOGGLES *********/
function initSubscribeAndSaveToggles() {
  if (window.__subscribeToggleBound) return;
  window.__subscribeToggleBound = true;

  const getQuantityForInput = (input) => {
    let quantity = Number.parseInt(input?.dataset?.quantity || '1', 10);
    if (!Number.isFinite(quantity) || quantity < 1) {
      const cartItem = input?.closest('.cart-item');
      const qtyInput = cartItem ? cartItem.querySelector('input[name="updates[]"]') : null;
      const qtyValue = qtyInput ? qtyInput.value : '1';
      quantity = Number.parseInt(qtyValue, 10);
    }
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    return quantity;
  };

  const updateSellingPlan = async ({ itemKey, quantity, sellingPlanId, inputEl }) => {
    if (!itemKey) return;
    if (inputEl) inputEl.disabled = true;
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      drawer.classList.add('is-loading');
      const loader = drawer.querySelector('.cart-drawer__loader');
      if (loader) loader.hidden = false;
    }

    try {
      const payload = {
        id: itemKey,
        quantity,
        selling_plan: sellingPlanId,
        sections: 'cart-drawer',
        properties: {}
      };

      if (sellingPlanId) {
        payload.selling_plan = sellingPlanId;
        payload.properties._subscription_toggle = 1;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const parsedState = await response.json();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState, true);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating subscription toggle:', error);
      if (inputEl) {
        inputEl.checked = !inputEl.checked;
        inputEl.disabled = false;
      }
    } finally {
      if (drawer) {
        drawer.classList.remove('is-loading');
        const loader = drawer.querySelector('.cart-drawer__loader');
        if (loader) loader.hidden = true;
      }
    }
  };

  document.addEventListener('change', async (event) => {
    const input = event.target;
    if (!input || !input.matches('.drawer-toggle__input')) return;

    const itemKey = input.dataset.itemKey;
    const quantity = getQuantityForInput(input);
    const availablePlans = (input.dataset.availablePlans || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);

    if (!itemKey) return;

    let targetPlanId = null;
    if (input.checked) {
      if (!availablePlans.length) return;
      const sixtyPlan = availablePlans.find((entry) => /60\s*-?\s*day/i.test(entry));
      const fallbackPlan = availablePlans;
      const selected = sixtyPlan || fallbackPlan;
      const parts = selected.split('|');
      targetPlanId = parts.length > 1 ? parseInt(parts[parts.length - 1], 10) : null;
      if (!targetPlanId) return;
    }

    await updateSellingPlan({ itemKey, quantity, sellingPlanId: targetPlanId, inputEl: input });
  });

  document.addEventListener('click', async (event) => {
    const editButton = event.target.closest('.drawer-subscribe-edit');
    if (editButton) {
      const container = editButton.closest('.drawer-subscribe-and-save')?.closest('.cart-item')?.querySelector('.drawer-subscribe-meta') || editButton.closest('.drawer-subscribe-meta');
      const dropdown = container ? container.querySelector('.drawer-subscribe-dropdown') : null;
      if (dropdown) {
        dropdown.hidden = !dropdown.hidden;
        editButton.textContent = dropdown.hidden ? 'Edit' : 'Cancel';
      }
      return;
    }

    const optionButton = event.target.closest('.drawer-subscribe-option');
    if (!optionButton) return;

    const container = optionButton.closest('.drawer-subscribe-meta');
    const itemKey = container ? container.dataset.itemKey : null;
    const toggleInput = container ? container.closest('.cart-item')?.querySelector('.drawer-toggle__input') : null;
    const quantity = getQuantityForInput(container || toggleInput);
    const sellingPlanId = Number.parseInt(optionButton.dataset.planId || '', 10);
    
    if (!Number.isFinite(sellingPlanId)) return;
    if (toggleInput) toggleInput.checked = true;
    
    await updateSellingPlan({ itemKey, quantity, sellingPlanId, inputEl: toggleInput });
  });
}


/********** RECHARGE BUNDLE CALLBACK ***********/
window.RechargeBundle = {
  onAddToCart: () => {
    console.log('Recharge Official Callback: Bundle Added');
    const drawer = document.querySelector('cart-drawer');
    
    if (drawer) {
      // 1. Wait 600ms to ensure database updates reflect inside Shopify server side
      setTimeout(() => {
        // 2. Clear out 'is-empty' class tags early to prevent layout hide quirks
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        drawer.classList.remove('is-empty');

        // 3. Re-render UI architecture elements
        drawer.renderContents(null, true);
        
        // 4. Update badge counter safely
        if (typeof fetchCartItemsCount === 'function') fetchCartItemsCount();
      }, 600); 
    } else {
      window.location.href = '/cart';
    }
  }
};


/********** BACK BUTTON REFRESH LOGIC **********/
window.addEventListener('pageshow', (event) => {
  // Check if target page layout loaded from browser hardware bfcache state
  const isBackNavigation = event.persisted || (window.performance && window.performance.navigation.type === 2);

  if (isBackNavigation) {
    console.log('Detected back navigation. Refreshing cart data...');
    const drawer = document.querySelector('cart-drawer');
    
    if (drawer) {
      // UPDATED: Sync cart parameters WITHOUT sliding target container panel visible
      drawer.renderContents(null, false);
    }
    
    if (typeof fetchCartItemsCount === 'function') {
      fetchCartItemsCount();
    }
  }
});


/** PREVENT CART PAGE AUTO-OPENS VIA REDIRECT PARAMETER 
 * Safely parses global location queries, updates window history, and resolves UI elements.
 */
(() => {
  const triggerAutoOpenSequence = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.has('open_cart')) {
      urlParams.delete('open_cart');
      const cleanedSearch = urlParams.toString();
      const cleanedURL = window.location.pathname + (cleanedSearch ? '?' + cleanedSearch : '');
      window.history.replaceState({}, document.title, cleanedURL);

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        drawer.classList.remove('is-empty');

        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents(null, true);
        } else if (typeof drawer.open === 'function') {
          drawer.open();
        }
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', triggerAutoOpenSequence);
  } else {
    triggerAutoOpenSequence();
  }
})();